/*

This is the Collection Duplicate Login Hook.
Purpose: We need a new UI sequence that takes a non-authed
user through a modal login, then continues the action that they
intended to take on the artwork they clicked.

1. Click on Duplicate link and enter the onClickDuplicate function
onClickDuplicate flow:
  a. Is the user logged in?
    1. If no, show the login modal and set the continueAfterLogin flag so we know to complete the duplicate action after login.

2. When the user is confirmed logged in, set the shouldBlockEvents flag and move into the collectionDuplicate function
collectionDuplicate flow:
  a. Use the duplicateCollection Api to initiate the duplication action. The apiResponse will be am object, returning a success status and a newIdUserCollection value.
  c. If the apiResponse has a success value of true,
    1. Pack setNewCollectionId with the new collecton ID to return in the hook payload.
    2. Fire the onSuccess callback function with a payload showing the action result

*/
import { useState, useEffect } from 'react'
import { duplicateCollection } from 'apis/private/collection'
type Props = {
  idUserCollection: number
  isAuthenticated: boolean
  onSuccess?: (...args: Array<any>) => any
  showModal: (...args: Array<any>) => any
  openModal: (...args: Array<any>) => any
  switchToCreateLogin: boolean
}
type HookProps = {
  onClickCollectionDuplicate: (...args: Array<any>) => any
  newCollectionId: number
}

const useCollectionDuplicate = (props: Props): HookProps => {
  const {
    isAuthenticated,
    idUserCollection,
    onSuccess,
    showModal,
    openModal,
    switchToCreateLogin,
  } = props
  const [shouldBlockEvents, setShouldBlockEvents] = useState(false) // Block UI

  const [continueAfterLogin, handlecontinueAfterLogin] = useState(false) // Should we fire event after LOGIN/REGISTER success

  const [newCollectionId, setNewCollectionId] = useState(0)
  // Open Modal and Now Login
  useEffect(() => {
    if (continueAfterLogin && !switchToCreateLogin) {
      // Make a call to update the EVENT
      handlecontinueAfterLogin(false)
      collectionDuplicate()
    }
  }, [isAuthenticated])

  const collectionDuplicate = async () => {
    const apiResponse = await duplicateCollection(idUserCollection)
    const { success, payload } = apiResponse
    const { newIdUserCollection } = payload

    if (apiResponse) {
      setShouldBlockEvents(false)

      if (success) {
        setNewCollectionId(newIdUserCollection)
      }

      if (onSuccess) {
        const successStatus = !!success
        onSuccess({
          shouldShowMessage: true,
          actionStatus: successStatus,
        })
      }
    }
  }

  const onClickCollectionDuplicate = (ev: React.SyntheticEvent<any>): void => {
    if (shouldBlockEvents) {
      return
    }

    if (!isAuthenticated) {
      openModal('login')
      handlecontinueAfterLogin(true)
      return
    }

    setShouldBlockEvents(true)
    collectionDuplicate()
  }

  return {
    onClickCollectionDuplicate,
    newCollectionId,
  }
}

export default useCollectionDuplicate
